  <template>
    <div
      v-if="error"
      class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4"
      role="alert"
      >
      <strong class="font-bold">Error! </strong>
      <span class="block sm:inline">{{ error }}</span>
    </div>

    <HowToAgent v-if="showHowToPage"
      :show-how-to-page="showHowToPage"
      :external-navigation="true"
      @show-how-to-close="handleShowHowToPage"
    />

    <div v-if="isStaff || canViewAgent" class="p-6">
      <!-- Header Section -->
      <div class="flex items-center justify-between mb-6">
        <h1 id="total_count" class="text-3xl font-bold text-gray-900 dark:text-white">
          Agents - {{ filteredAgents.length }}
          <span v-if="isFiltered" class="text-sm text-gray-500">(filtered)</span>
          <button
            v-if="failingAgentCount"
            type="button"
            @click="toggleFailedFilter"
            :title="selectedFilters.updateFilter === 'failed' ? 'Show all agents' : 'Show only agents that failed to update'"
            :class="[
              'ml-3 inline-flex items-center rounded-full px-2.5 py-0.5 text-sm font-semibold align-middle transition-colors',
              selectedFilters.updateFilter === 'failed'
                ? 'bg-red-500 text-white ring-2 ring-red-400 hover:bg-red-600'
                : 'bg-red-500/20 text-red-500 hover:bg-red-500/30',
            ]"
          >
            {{ failingAgentCount }} Failed to Update
          </button>
        </h1>
        <div class="text-base flex space-x-3 justify-between">
          <button
            type="button"
            class="flex items-center gap-2 animate-shake hover:bg-gray-100 dark:hover:bg-gray-800 bg-transparent px-3 py-2 rounded-lg transition-colors"
            @click="startTutorial"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-question-mark dark:text-gray-50 text-gray-900">
              <circle cx="12" cy="12" r="10"/>
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
              <path d="M12 17h.01"/>
            </svg>
            <span class="text-sm font-medium dark:text-gray-50 text-gray-900">Tutorial</span>
          </button>
           <button v-if="isStaff || canAddAgent"
            id="add"
            @click="
              $router.push({ name: 'AgentDetails', params: { id: 'new' } })
            "
            class="btn bg-blue-700 text-white text-sm font-medium hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Add Agent
          </button>
        </div>
      </div>

      <!-- Search and Filter Section -->
      <div
        class="flex flex-col md:flex-row md:items-center md:justify-between mb-6"
      >
        <!-- Search Bar -->
        <div class="mb-4 md:mb-0 md:flex-1 md:mr-4">
          <SearchBar
            v-model="searchQuery"
            placeholderText="Search agents by organisation..."
          />
        </div>

        <div class="mb-4 md:hidden">
          <button
            @click="showFilters = !showFilters"
            class="bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300"
          >
            {{ showFilters ? "Hide Filters" : "Show Filters" }}
          </button>
        </div>
        
        <!-- Filters Section -->
        <div
          :class="[
            'md:flex md:items-center ',
            showFilters ? 'block' : 'hidden md:block',
          ]"
        >
          <FiltersSection
            :filters="dynamicFilters"
            v-model:selectedFilters="selectedFilters"
            :showFilters="showFilters"
            @update:showFilters="showFilters = $event"
            @applyFilters="applyFilters"
          />
        </div>

        <!-- Bulk Action Section -->
        <div class="flex items-center mt-4 md:mt-0 space-x-2 ml-2">
          <GlassSelect
            id="bulkactions"
            v-model="bulkAction"
            :options="(isStaff || canDeleteAgent) ? [{id:'delete',name:'Delete selected items'}] : []"
            placeholder="Bulk Actions"
          />
          <button
            id="apply"
            v-if="selectedTab=='agents'"
            @click="applyBulkAction"
            :disabled="!bulkAction || selectedAgents.length === 0"
            :class="[
              'btn focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200',
              bulkAction && selectedAgents.length
                ? 'bg-red-600 text-white hover:bg-red-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed',
            ]"
          >
            Apply
          </button>
          <button
            v-if="selectedTab=='groups'"
            id="apply"
            @click="applyBulkAction"
            :disabled="!bulkAction || selectedGroups.length === 0"
            :class="[
              'px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200',
              bulkAction && selectedGroups.length
                ? 'bg-red-600 text-white hover:bg-red-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed',
            ]"
          >
            Apply
          </button>
        </div>
      </div>

      <div class="my-2 backdrop-blur-sm py-3 px-3 glass-card-lite">
        <div class="-mb-px flex space-x-8 overflow-x-auto">
          <!-- Agents tab -->
          <button
            id="agent_tab"
            @click="selectTab('agents')"
            :class="[
            'px-4 py-2 font-bold transition-all duration-200 flex items-center space-x-2 rounded-md border border-white glass-card',
            selectedTab === 'agents'
                ? 'bg-transparent text-fuchsia-500'
                : 'text-gray-400 hover:bg-slate-800/30 hover:text-fuchsia-500 bg-transparent'
            ]"
          >
            Agents  ({{ agents.length }})
          </button>

          <!-- Groups tab -->
          <button
            id="installer_tab"
            @click="selectTab('groups')"
            :class="[
              'px-4 py-2 font-bold transition-all duration-200 flex items-center space-x-2 rounded-md border border-white glass-card',
              selectedTab === 'groups'
                  ? 'bg-transparent text-fuchsia-500'
                  : 'text-gray-400 hover:bg-slate-800/30 hover:text-fuchsia-500  bg-transparent'
              ]"
          >
            Mivu Installers ({{ groupInstallers.length }})
          </button>

          <!-- Downloads tab -->
          <button
            id="downloads_tab"
            @click="selectTab('downloads')"
            :class="[
              'px-4 py-2 font-bold transition-all duration-200 flex items-center space-x-2 rounded-md border border-white glass-card',
              selectedTab === 'downloads'
                  ? 'bg-transparent text-fuchsia-500'
                  : 'text-gray-400 hover:bg-slate-800/30 hover:text-fuchsia-500  bg-transparent'
              ]"
          >
            My Builds ({{ buildJobs.length }})
            
          </button>
        </div>
      </div>

      <!-- Organizations Table -->
      <GenericTable v-if="selectedTab == 'agents'"
        ref="table"
        :columns="columns"
        :items="paginatedAgents"
        itemKey="licensekey"
        :selectable="true"
        @update:selectedItems="updateSelectedAgents"
        @rowClick="goToAgentDetail"
      >
        <!-- Organisation cell with group marker -->
        <template #organization_name="{ item, value }">
          <div class="flex items-center gap-2">
            <span>{{ value }}</span>
            <span
              v-if="item.group"
              class="inline-flex items-center gap-1 rounded-full bg-fuchsia-500/20 text-fuchsia-500 px-2 py-0.5 text-xs font-semibold whitespace-nowrap"
              :title="`Group: ${item.group_name}`"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-users">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
              {{ item.group_name }}
            </span>
          </div>
        </template>

        <!-- Custom cell templates -->
        <template #created_at="{ value }">
          {{ formatDateTime(value) }}
        </template>
      </GenericTable>

      <!-- Organizations Table -->
      <GenericTable
        v-if="selectedTab == 'groups'"
        ref="table"
        :columns="columnsGroup"
        :items="paginatedGroups"
        itemKey="id"
        :selectable="true"
        @update:selectedItems="updateSelectedGroups"
      >

         <!-- Custom header for Type column -->
        <template #head(type)>
          <div class="flex flex-col items-start">
            <GlassSelect
              v-model="filterType"
              :options="[{id:'Organisation',name:'Organisation'},{id:'Group',name:'Group'}]"
              placeholder="All"
            />
          </div>
        </template>

        <template #link="{value}">
          <button
              class="inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              @click="downloadGroupFile(value)"
            >
            <div class="flex space-x-2">
              <p class="font-medium">Download MivuInstaller</p>
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-arrow-down-to-line-icon lucide-arrow-down-to-line"><path d="M12 17V3"/><path d="m6 11 6 6 6-6"/><path d="M19 21H5"/></svg>
            </div>
          </button>
          <a ref="groupdownloadLink" style="display:none;"></a>
        </template>


        <!-- Custom cell templates -->
        <template #created_at="{ value }">
          {{ formatDateTime(value) }}
        </template>

        <template v-if="selectedTab == 'groups'"#created_date="{ value }">
          {{ formatDateTime(value) }}
        </template>
      </GenericTable>

      <!-- Downloads / current builds -->
      <div v-if="selectedTab == 'downloads'" class="space-y-3">
        <div class="flex justify-end" v-if="buildJobs.some(j => j.status === 'completed' || j.status === 'failed')">
          <button
            @click="clearCompletedJobs"
            class="px-3 py-1.5 text-sm rounded-md bg-gray-800 text-gray-100 hover:bg-gray-900 transition duration-200"
          >
            Clear finished
          </button>
        </div>

        <div
          v-if="buildJobs.length === 0"
          class="glass-card text-center p-8 text-gray-600 dark:text-gray-300"
        >
          No installer builds in progress. Generate a MivuInstaller from an agent to track it here.
        </div>

        <div
          v-for="job in buildJobs"
          :key="job.id"
          class="glass-card flex items-center justify-between p-4"
        >
          <div class="min-w-0">
            <p class="font-semibold text-gray-800 dark:text-gray-50 truncate">{{ job.label }}</p>
            <p class="text-sm text-gray-500 dark:text-gray-400 truncate">{{ job.filename }}</p>
            <p v-if="job.status === 'failed' && job.error" class="text-sm text-red-500 mt-1">{{ job.error }}</p>
          </div>

          <div class="flex items-center space-x-3 shrink-0">
            <span
              class="inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold"
              :class="jobStatusClass(job.status)"
            >
              <svg
                v-if="!['completed','failed'].includes(job.status)"
                class="animate-spin h-3 w-3 mr-1.5"
                xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
              >
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
              </svg>
              {{ jobStatusLabel(job.status) }}
            </span>
            <button
              v-if="job.status === 'completed' && job.productId"
              @click="downloadBuildFile(job)"
              class="inline-flex items-center justify-center rounded-lg px-3 py-1.5 text-sm font-medium text-white bg-transparent hover:bg-transparent shadow focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <Download class="text-gray-900 dark:text-white h-5"/>
            </button>

            <button
              v-if="['completed','failed'].includes(job.status)"
              @click="removeBuildJob(job.id)"
              class="inline-flex items-center justify-center rounded-lg px-3 py-1.5 text-sm font-medium text-white bg-transparent hover:bg-transparent focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              <Trash2Icon class="text-red-500 h-5"/>
            </button>
          </div>
        </div>
        <a ref="buildDownloadLink" style="display:none;"></a>
      </div>

      <!-- Pagination -->
      <div
        v-if="selectedTab != 'downloads'"
        class="flex flex-col md:flex-row items-center justify-between mt-8 space-y-4 md:space-y-0"
      >
        <div class="flex items-center space-x-2">
          <button
            @click="changePage(-1)"
            :disabled="currentPage === 1"
            class="px-4 py-2 rounded-md bg-gray-800 text-gray-100 hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
          >
            Previous
          </button>
          <button
            @click="changePage(1)"
            :disabled="currentPage === totalPages"
            class="px-4 py-2 rounded-md bg-gray-800 text-gray-100 hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
          >
            Next
          </button>
        </div>
        <div class="text-gray-700 dark:text-white">
          Page {{ currentPage }} of {{ totalPages }}
        </div>
        <div>
          <GlassSelect
            v-model="itemsPerPage"
            :options="[{id:10,name:'10 per page'},{id:20,name:'20 per page'},{id:50,name:'50 per page'},{id:100,name:'100 per page'},{id:-1,name:'All'}]"
            placeholder="Items per page"
            @change="updatePagination"
          />
        </div>
      </div>

      <DeleteModal
        v-if="deletingItem"
        :deleting-item="deletingItem"
        :componet-objects="componetObjects"
        :is-deleting="isDeleting"
        @delete="confirmDelete"
        @close="deletingItem=false"
      />
    </div>
  </template>

  <script>
  import { getAgents, deleteAgent } from "@/services/agentService";
  import { getOrgs } from "@/services/wsOrgService";
  import { getNoAgentProducts, getNoAgentProductById, deleteNoAgentProduct } from "@/services/productsNoAgentService";
  import { getProductById } from "@/services/productsService";
  import { getAgentGroups } from "@/services/agentGroupService";
  import SearchBar from "@/components/SearchBar.vue";
  import GenericTable from "@/components/GenericTable.vue";
  import { useStore } from "@/store";
  import { buildJobsStore } from "@/store/buildJobs";
  import HowToAgent from "./HowToAgent.vue";
  import FiltersSection from "@/components/FiltersSection.vue"
  import DeleteModal from "@/components/DeleteModal.vue";
  import { useTutorial } from "@/composables/tutorial";
  import GlassSelect from "@/components/GlassSelect.vue";
  import { Trash2Icon, Download } from "lucide-vue-next";

  export default {
    name: "AgentView",
    setup() {
      const { start } = useTutorial();
      return { _start: start };
    },
    components: {
      SearchBar,
      GenericTable,
      HowToAgent,
      FiltersSection,
      DeleteModal,
      GlassSelect,
      Trash2Icon,
      Download
    },
    data() {
      return {
        showFilters: false,
        filterType: "",
        showHowToPage: false,
        agents: [],
        groups: [],
        selectedTab: "agents",
        orgInstaller: null,
        groupInstallers: [],
        filteredGroupInstallers: null,
        searchQuery: "",
        filteredAgents: [],
        currentPage: 1,
        itemsPerPage: 10,
        selectedAgents: [],
        selectedGroups: [],
        deletingItem: false,
        bulkAction: "",
        columns: [
            { key: "licensekey", label: "License Key", sortable: true, width: "25%" },
            { key: "organization_name", label: "Organisation", sortable: true, width: "25%" },
            { key: "license_state", label: "State", sortable: true, width: "25%" },
            { key: "distro", label: "Distro", sortable: true, width: "25%" },
            { key: "host_name", label: "Hostname", sortable: true, width: "25%" },
            { key: "user_name", label: "Username", sortable: true, width: "25%" },
            { key: "created_at", label: "Created Date", sortable: true, width: "25%" },
        ],
        columnsGroup: [
            { key: "type",             label: "Type",           sortable: true, width: "14%" },
            { key: "name",             label: "Name",           sortable: true, width: "14%" },
            { key: "operating_system", label: "OS",             sortable: true, width: "14%" },
            { key: "installer_type",   label: "Installer Type", sortable: true, width: "14%" },
            { key: "architecture",     label: "Architecture",   sortable: true, width: "14%" },
            { key: "link",             label: "MivuInstaller",  sortable: true, width: "14%" },
            { key: "created_date",     label: "Created Date",   sortable: true, width: "16%" },
        ],
        error: "",
        organizations: [],
        products: [],
        canViewAgent: false,
        canDeleteAgent: false,
        canAddAgent: false,
        dynamicFilters: [],
        selectedFilters: {
          organizationFilter: "",
          osFilter: "",
          groupFilter: "",
          updateFilter: "",
        },
        userOrg: "",
        isDeleting: false,
        steps: [
          {
            element: '#total_count',
            action: 'display',
            value: '',
            message: 'Shows the total number of items in the table.'
          },
          {
            element: '#searchbar',
            action: 'display',
            value: '',
            message: 'Search and filter agents or installers.'
          },
          {
            element: '#agent_tab',
            action: 'display',
            value: '',
            message: 'View and manage your agents.'
          },
          {
            element: '#installer_tab',
            action: 'display',
            value: '',
            message: 'Access and download generated MivuInstallers.'
          },
          {
            element: '#table',
            action: 'display',
            value: '',
            message: 'Displays all agents and MivuInstallers.'
          },
          {
            element: '#bulkactions',
            action: 'display',
            value: 1,
            message: 'Select one or more items to enable bulk actions.'
          },
          {
            element: '#apply',
            action: 'display',
            message: 'Apply selected bulk actions (delete, archive, or modify).'
          },
          {
            element: '#download_button',
            action: 'display',
            message: 'Download an organisational MivuInstaller.'
          },
          {
            element: '#add',
            action: 'display',
            message: 'Create a new MivuInstaller.'
          }
        ],
      };
    },
    computed: {
      buildJobs() {
        return buildJobsStore.jobs.value;
      },
      activeBuilds() {
        return buildJobsStore.activeCount();
      },
      isStaff(){
        return useStore.isStaff();
      },
      user() {
        return useStore.getUser();
      },
      isFiltered() {
        return (
          this.searchQuery !== "" ||
          this.selectedFilters.organizationFilter !== "" ||
          this.selectedFilters.osFilter !== "" ||
          this.selectedFilters.groupFilter !== "" ||
          this.selectedFilters.updateFilter !== ""
        );
      },
      failingAgentCount() {
        return this.filteredAgents.filter((agent) => agent.last_update_status === "failed").length;
      },
      totalPages() {
        return this.itemsPerPage === -1
          ? 1
          : Math.ceil(this.filteredAgents.length / this.itemsPerPage);
      },
      paginatedAgents() {
        if (this.itemsPerPage === -1) {
          return this.filteredAgents;
        }
        const start = (this.currentPage - 1) * this.itemsPerPage;
        const end = start + this.itemsPerPage;
        return this.filteredAgents.slice(start, end);
      },
      paginatedGroups() {
        if (this.itemsPerPage === -1) {
          return this.filteredGroups;
        }
        const start = (this.currentPage - 1) * this.itemsPerPage;
        const end = start + this.itemsPerPage;
        return this.filteredGroups.slice(start, end);
        // return this.filteredGroups;
      },
      filteredGroups() {
        const base = this.filteredGroupInstallers ?? this.groupInstallers;
        if (!this.filterType) return base;
        return base.filter(item => item.type === this.filterType);
      },
      componetObjects() {
        if (this.selectedTab === "agents"){
          return this.selectedAgents.map(agent => {
            return {
              values: [
                { name: "Agent", value: `${agent.licensekey} - ${agent.organization_name}` },
              ]
            }
          })
        }
        else{
          return this.selectedGroups.map(installer => {
            return {
              values: [
                { name: "MivuInstaller", value: `${installer.organization_name}-${installer.installer_type}` },
              ]
            }
          })
        }
        
      },
    },
    watch: {
      searchQuery() {
        this.applyFilters();
      },
      user: {
        handler(user) {
          if (user) {
            this.checkPermissions();
          }
        },
        immediate: true
      },
    },
    async mounted() {
      this.initSelectedTab();
      this.checkPermissions();
      await this.waitForStoreReady();
      await this.loadData();
      this.initializeFilters();
    },
    methods: {
      async startTutorial() {
        await this._start(this.steps);
      },
      async waitForStoreReady() {
        const maxAttempts = 50;
        let attempts = 0;
        
        while (!useStore.userOrg() && attempts < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, 100));
          attempts++;
        }
      },
      async loadData(){
        await Promise.all([this.fetchOrganisations(), this.fetchGroups()]);
        await Promise.all([this.fetchAgent(), this.fetchNoAgentProducts()]);
        this.applyFilters();
      },
      handleShowHowToPage(value){
        this.showHowToPage = value;
      },
      async fetchOrganisations(){
        try {
          this.organizations = await getOrgs();
        } catch (error) {
          console.error(error);
        }
      },
      async fetchGroups(){
        try {
          this.groups = await getAgentGroups();
        } catch (error) {
          console.error(error);
        }
      },
      async fetchNoAgentProducts(){
        try {
          const products = await getNoAgentProducts();
          this.products = products;
          this.orgInstaller = products.find(item => item.group == null);
          this.groupInstallers =  products.filter(item => item.organization);

          this.groupInstallers.forEach(installer => {
            installer.organization_name = this.organizations.find(item => item.id === installer?.organization)?.name;
            installer.name = this.groups.find(item => item.id === installer?.group)?.name ||
             this.organizations.find(item => item.id === installer?.organization)?.org_key;
            installer.type = installer.group ? "Group" : "Organisation";
          });

        } catch (error) {
          console.error("Error fetching installer:", error);
        }
      },
      downloadFile() {
        if (this.orgInstaller && this.orgInstaller.link) {
          const link = this.$refs.downloadLink;
          link.href = this.orgInstaller.link;
          link.download = '';
          link.click();
        }
      },
      downloadGroupFile(url){
        if (url) {
          const a = this.$refs.groupdownloadLink;
          a.href = url;
          a.download = '';
          a.click();
        }
      },
      initSelectedTab() {
        const tab = this.$route.query.tab;
        const validTabs = ["agents", "groups", "downloads"];
        this.selectedTab = validTabs.includes(tab) ? tab : "agents";
      },
      specificTabUrl() {
        this.$router.push({
          name: "AgentView",
          query: { tab: this.selectedTab }
        });
      },
      selectTab(item){
        this.selectedTab = item;
        this.specificTabUrl();
        this.loadData()
      },
      async downloadBuildFile(job){
        if (!job?.productId) return;
        try {
          const product = job.productType === "Agent"
            ? await getProductById(job.productId)
            : await getNoAgentProductById(job.productId);
          const url = product?.link;
          if (!url) return;
          const a = this.$refs.buildDownloadLink;
          a.href = url;
          a.download = '';
          a.click();
        } catch (error) {
          console.error("Failed to fetch installer for download:", error);
        }
      },
      removeBuildJob(id){
        buildJobsStore.removeJob(id);
      },
      clearCompletedJobs(){
        buildJobsStore.clearCompleted();
      },
      jobStatusLabel(status){
        return {
          building: 'Building',
          'fetching-manifest': 'Fetching manifest',
          'downloading-files': 'Downloading files',
          preparing: 'Preparing',
          compiling: 'Compiling',
          uploading: 'Uploading to server',
          downloading: 'Downloading',
          saving: 'Saving',
          completed: 'Completed',
          failed: 'Failed',
        }[status] || status;
      },
      jobStatusClass(status){
        if (status === 'completed') return 'bg-green-500/20 text-green-500';
        if (status === 'failed') return 'bg-red-500/20 text-red-500';
        return 'bg-green-500/20 text-green-500';
      },
      checkPermissions(){
        this.canAddAgent = useStore.hasPermission("Can add agents");
        this.canDeleteAgent = useStore.hasPermission("Can delete agents");
        this.canViewAgent = useStore.hasPermission("Can view agents");
      },
      async fetchAgent() {
      try {

        this.agents = await getAgents();

        this.agents.forEach(agent => {
            agent.organization_name = this.organizations.find(item => item.id === agent.organization)?.name;
            agent.host_name = agent.hostname || 'unregistered';
            agent.user_name = agent.username || 'unregistered';
            agent.group_name = agent.group
              ? (this.groups?.find(item => item.id === agent.group)?.name || 'Group')
              : null;
        });
      } catch (error) {
        console.error("Error fetching agents:", error);
        if (error.response) {
          switch (error.response.status) {
            case 404:
              this.error = error.response.status +" The resource was not found. Please check the API endpoint."
              break;
            case 401:
              this.error = error.response.status + " Unauthorized access. Please check your authentication token or log in again."
              break;
            case 403:
              this.error = error.response.status + " You don't have permission to access this resource."
                          + " Contact a staff member to assign you the permissions to access this page. ";
              break;
            default:
              this.error = error.response.status =   error.response.data.detail || "Unknown error"
          }
        } else if (error.request) {
          alert(
            "No response received from the server. Please check your internet connection."
          );
        } else {
          alert(`An error occurred: ${error.message}`);
        }
      }
    },
    goToAgentDetail(agent) {
      this.$router.push({
        name: "AgentDetails",
        params: { id: agent.licensekey },
      });
    },
    updateSelectedAgents(selectedItems) {
      this.selectedAgents = selectedItems;
    },
    updateSelectedGroups(selectedItems) {
      this.selectedGroups = selectedItems;
    },
    async applyBulkAction() {
      if (this.bulkAction === "delete" ) {
        this.deletingItem = true;
      }
      this.bulkAction = "";
    },
    async confirmDelete() {
      this.isDeleting = true;
      if (this.selectedTab == "agents"){
        await this.deleteSelectedAgents();
      } else {
        await this.deleteSelectedGroupInstallers();
      }
      this.deletingItem = false; 
      this.bulkAction = "";
    },
    async deleteSelectedAgents() {
      if (this.selectedAgents.length === 0) {
        alert("No agents selected for deletion");
        return;
      }
      try {
        const deletePromises = this.selectedAgents.map((agent) =>
          deleteAgent(agent.licensekey)
        );
        await Promise.all(deletePromises);

        this.agents = this.agents.filter(
          (agent) => !this.selectedAgents.some((item) => item.licensekey === agent.licensekey)
        );
        this.$refs.table.clearSelection();
        this.applyFilters();
        this.selectedAgents = [];
      } catch (error) {
        console.error("Error during bulk delete:", error);
        alert(
          "An error occurred while deleting agents. Please try again."
        );
      }  finally {
        this.isDeleting = false;
      }
    },
    async deleteSelectedGroupInstallers() {
      if (this.selectedGroups.length === 0) {
        alert("No agents selected for deletion");
        return;
      }
      try {
        const deletePromises = this.selectedGroups.map((installer) =>
          deleteNoAgentProduct(installer.id)
        );
        await Promise.all(deletePromises);

        this.groupInstallers = this.groupInstallers.filter(
          (installer) => !this.selectedGroups.some((item) => item.id === installer.id)
        );
        this.$refs.table.clearSelection();
        this.applyFilters();
        this.selectedGroups = [];
      } catch (error) {
        console.error("Error during bulk delete:", error);
        alert(
          "An error occurred while deleting installers. Please try again."
        );
      } finally {
        this.isDeleting = false;
      }
    },
    formatDateTime(dateString) {
      const options = {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        timeZoneName: "short",
      };
      return new Date(dateString).toLocaleString(undefined, options);
    },
    toggleFailedFilter() {
      this.selectedFilters.updateFilter =
        this.selectedFilters.updateFilter === "failed" ? "" : "failed";
      if (this.selectedTab !== "agents") this.selectTab("agents");
      this.applyFilters();
    },
    applyFilters() {
      const q = this.searchQuery.toLowerCase();
      this.filteredAgents = this.agents.filter((agent) => {
        const matchesOrgName = (agent?.organization_name || '').toLowerCase().includes(q);
        const matchesHostname = (agent?.hostname || '').toLowerCase().includes(q);
        const matchesUserName = (agent?.username || '').toLowerCase().includes(q);
        const matchesOrg = this.selectedFilters.organizationFilter === "" ||
          agent.organization === this.selectedFilters.organizationFilter;
        const groupFilter = this.selectedFilters.groupFilter;
        const matchesGroup =
          groupFilter === "" ||
          (groupFilter === "none" ? !agent.group : agent.group === groupFilter);
        const matchesUpdate =
          this.selectedFilters.updateFilter === "" ||
          agent.last_update_status === this.selectedFilters.updateFilter;
        return (matchesOrgName || matchesHostname || matchesUserName) && matchesOrg && matchesGroup && matchesUpdate;
      });
      this.filteredGroupInstallers = this.groupInstallers.filter((installer) => {
        const matchesInstallerOrg = (installer?.organization_name || '').toLowerCase().includes(q);
        const matchesOrg = this.selectedFilters.organizationFilter === "" ||
          installer.organization === this.selectedFilters.organizationFilter;
        const matchesOs =
          this.selectedFilters.osFilter === "" ||
          installer.operating_system === this.selectedFilters.osFilter;
        return matchesInstallerOrg && matchesOrg && matchesOs;
      });
      this.currentPage = 1;
    },
    changePage(delta) {
      this.currentPage += delta;
    },
    updatePagination() {
      this.currentPage = 1;
    },
    initializeFilters() {
        this.dynamicFilters = [
          {
            name: "organizationFilter",
            label: "Organisation",
            options: [{ value: "", label: "All" }].concat(
              Object.keys(this.organizations).map((orgId) => ({
                value: this.organizations[orgId]?.id,
                label: this.organizations[orgId]?.name,
              }))
            ),
          },
          {
            name: "osFilter",
            label: "OS",
            options: [
              { value: "", label: "All" },
              { value: "Windows", label: "Windows" },
              { value: "Linux", label: "Linux" },
            ],
          },
          {
            name: "groupFilter",
            label: "Group",
            options: [
              { value: "", label: "All" },
              { value: "none", label: "No Group" },
            ].concat(
              (this.groups || []).map((group) => ({
                value: group.id,
                label: group.name,
              }))
            ),
          },
          {
            name: "updateFilter",
            label: "Update",
            options: [
              { value: "", label: "All" },
              { value: "failed", label: "Failed" },
            ],
          },
        ];
      },
    },
  };
  </script>
